// StickysViewMain.js - Updated to use unified StickyNote component
import React, { useRef, useState, useEffect, useCallback } from 'react';
import ZIndexManagerMain from '../services/ZIndexManagerMain';
import { useStickies } from './contexts/StickiesContext';
import styles from './StickysViewMain.module.css';
import StickyNote from './StickyNote';  // Import unified StickyNote component
import ChecklistSticky from './ChecklistSticky';  // Import ChecklistSticky component
import StickiesListPanel from './MainPanel/StickiesListPanelMain';
import { usePanelContext } from './contexts/PanelContext';
import DraggableFreezeButton from './DraggableFreezeButton'; // Add this import
import { useNotes } from './contexts/NotesContext'; // Add this import
import DraggableAIButton from './DraggableAIButton';
import AICompositionModal from './AICompositionModal';
import NotePreviewSticky from './NotePreviewSticky';  // Add this line


const zManagerInstance = new ZIndexManagerMain();

const StickysViewMain = ({ selectedNoteId }) => {
  const { stickies, updateSticky, removeSticky, addSticky, saveStickyLayout, reorderStickies } = useStickies();
  const [stickyType, setStickyType] = useState('regular');
  const zManagerRef = useRef(zManagerInstance);
  const { getSelectedNote } = useNotes(); // Add this to access note info
  const { isRightPanelVisible, isLeftPanelVisible } = usePanelContext();
  const [isAICompositionModalOpen, setIsAICompositionModalOpen] = useState(false);
  const [arrangementMode, setArrangementMode] = useState('grid'); // 'grid', 'featured', 'cascade'
  const [showNotePreview, setShowNotePreview] = useState(true);  // Add this line
  

  // 2. Add the arrangement functions (adapted for main_x, main_y coordinates)
const arrangeStickiesInGrid = () => {
  if (!stickies || stickies.length === 0) return;
  
  console.log("Starting grid arrangement in main panel...");
  
  // Configuration for the grid
  const itemsPerRow = 3;
  const horizontalGap = 30;
  const verticalGap = 30;
  const standardWidth = 400;
  const standardHeight = 180;
  const startX = 1;
  const startY = 100;
  
  // Filter to only visible stickies (not in list mode)
  const visibleStickies = stickies.filter(
    sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
  );
  
  // Create updates for each sticky
  const updates = visibleStickies.map((sticky, index) => {
    const row = Math.floor(index / itemsPerRow);
    const col = index % itemsPerRow;
    
    const main_x = col * (standardWidth + horizontalGap) + startX;
    const main_y = row * (standardHeight + verticalGap) + startY;
    
    // Adjust dimensions based on sticky type
    let main_width = standardWidth;
    let main_height = standardHeight;
    
    if (sticky.type === 'ai') {
      main_width = 280;
      main_height = 220;
    } else if (sticky.type === 'checklist') {
      main_width = 240;
      main_height = 250;
    }
    
    // Update sticky positions directly
    updateSticky(sticky.id, {
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      is_minimized: false // Add this line
    });
    
    return {
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    };
  });
  
  saveStickyLayout(updates);
};

const arrangeStickiesFeatured = () => {
  if (!stickies || stickies.length === 0) return;
  
  const horizontalGap = 30;
  const verticalGap = 30;
  const standardWidth = 400;
  const standardHeight = 180;
  const startX = 1;
  const startY = 100;
  
  const visibleStickies = stickies.filter(
    sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
  );
  
  const updates = visibleStickies.map((sticky, index) => {
    let main_x, main_y, main_width, main_height;
    
    if (index < 4) {
      // First 4 stickies in top row
      main_x = index * (standardWidth + horizontalGap) + startX;
      main_y = startY;
      main_width = standardWidth;
      main_height = standardHeight;
    } else {
      // Remaining stickies: 2 per row
      const rowIndex = Math.floor((index - 4) / 2); // Which row (0, 1, 2...)
      const colIndex = (index - 4) % 2; // Position in row (0 or 1)
      
      main_x = colIndex * (standardWidth * 1.5 + horizontalGap) + startX;
      main_y = startY + standardHeight + verticalGap + rowIndex * (standardHeight + verticalGap);
      main_width = standardWidth * 1.5; // Make them wider
      main_height = standardHeight;
    }
    
    // Adjust for sticky type
    if (sticky.type === 'ai') {
      main_width = main_width * 1.1;
      main_height = main_height * 1.1;
    } else if (sticky.type === 'checklist') {
      main_height = main_height * 1.2;
    }
    
    updateSticky(sticky.id, {
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      is_minimized: false // Add this line

    });
    
    return {
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    };
  });
  
  saveStickyLayout(updates);
};

const arrangeStickiesCascade = () => {
  if (!stickies || stickies.length === 0) return;
  
  const startX = 1;
  const startY = 100;
  const xOffset = 40;
  const yOffset = 40;
  const standardWidth = 400;
  const standardHeight = 180;
  const maxCascadePerColumn = 6; // Limit to 6 per cascade column
  const columnSpacing = 500; // Space between cascade columns
  
  const visibleStickies = stickies.filter(
    sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
  );
  
  const updates = visibleStickies.map((sticky, index) => {
    const columnIndex = Math.floor(index / maxCascadePerColumn);
    const positionInColumn = index % maxCascadePerColumn;
    
    const main_x = startX + (columnIndex * columnSpacing) + (positionInColumn * xOffset);
    const main_y = startY + (positionInColumn * yOffset);
    
    let main_width = standardWidth;
    let main_height = standardHeight;
    
    if (sticky.type === 'ai') {
      main_width = 280;
      main_height = 220;
    } else if (sticky.type === 'checklist') {
      main_width = 240;
      main_height = 250;
    }
    
    updateSticky(sticky.id, {
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      is_minimized: false // Add this line
    });
    
    return {
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    };
  });
  
  saveStickyLayout(updates);
};

const arrangeStickiesTwoColumns = () => {
  if (!stickies || stickies.length === 0) return;
  
  const horizontalGap = 30;
  const verticalGap = 30;
  const standardWidth = 690;
  const standardHeight = 180;
  const columnSpacing = standardWidth + horizontalGap;
  
  // Move much further to the left - adjust this value as needed
  const startX = window.innerWidth - (2 * standardWidth + horizontalGap + 400); // Changed from 100 to 400
  const startY = 100;
  
  const visibleStickies = stickies.filter(
    sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
  );
  
  const updates = visibleStickies.map((sticky, index) => {
    const columnIndex = index % 2; // Alternates between 0 and 1 (left/right column)
    const rowIndex = Math.floor(index / 2); // Which row in the column
    
    const main_x = startX + (columnIndex * columnSpacing);
    const main_y = startY + (rowIndex * (standardHeight + verticalGap));
    
    let main_width = standardWidth;
    let main_height = standardHeight;
    
    // Adjust for sticky type
    if (sticky.type === 'ai') {
      main_width = 280;
      main_height = 220;
    } else if (sticky.type === 'checklist') {
      main_width = 240;
      main_height = 250;
    }
    
    updateSticky(sticky.id, {
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      is_minimized: false // Add this line
    });
    
    return {
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: main_x,
      main_y: main_y,
      main_width: main_width,
      main_height: main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    };
  });
  
  saveStickyLayout(updates);
};

const arrangeStickiesMinimizedCascade = () => {
  if (!stickies || stickies.length === 0) return;
  
  const startX = 300;
  const startY = 120;
  const xOffset = 60; // Horizontal offset so expanded ones don't overlap
  const yOffset = 80; // Vertical spacing - enough room for expansion
  const barHeight = 100;
  
  const visibleStickies = stickies.filter(
    sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
  );
  
  const updates = visibleStickies.map((sticky, index) => {
    const main_x = startX + (index * xOffset); // Each one offset horizontally
    const main_y = startY + (index * yOffset); // Plus vertical spacing for expansion room
    const zIndex = 1000 + index;
    
    updateSticky(sticky.id, {
      main_x: main_x,
      main_y: main_y,
      main_width: 600,
      main_height: barHeight,
      is_minimized: true,
      zIndex: zIndex
    });
    
    return {
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: main_x,
      main_y: main_y,
      main_width: 600,
      main_height: barHeight,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: zIndex
    };
  });
  
  saveStickyLayout(updates);
};

// 3. Add the cycle function
const cycleArrangements = () => {
  if (!stickies || stickies.length === 0) return;
  
  console.log("Cycling to next arrangement in main panel...");
  
  let nextMode;
  switch (arrangementMode) {
    case 'grid':
      nextMode = 'featured';
      arrangeStickiesFeatured();
      break;
    case 'featured':
      nextMode = 'cascade';
      arrangeStickiesCascade();
      break;
    case 'cascade':
      nextMode = 'two-columns';
      arrangeStickiesTwoColumns();
      break;
    case 'two-columns':
      nextMode = 'minimized-cascade';
      arrangeStickiesMinimizedCascade();
      break;
    case 'minimized-cascade':
    default:
      nextMode = 'grid';
      arrangeStickiesInGrid();
      break;
  }
  
  setArrangementMode(nextMode);
};
  
  // State for subnote detection
  const [isSubnote, setIsSubnote] = useState(false);

  // Check if current note is a subnote when selectedNoteId changes
  useEffect(() => {
    if (selectedNoteId) {
      const currentNote = getSelectedNote();
      setIsSubnote(currentNote && currentNote.parent_id !== null);
    }
  }, [selectedNoteId, getSelectedNote]);
  
  const [displayModes, setDisplayModes] = useState(() => {
    return stickies?.reduce((modes, sticky) => ({
      ...modes,
      [sticky.id]: sticky.display_mode || 'both'
    }), {}) || {};
  });

  const [selectedStickies, setSelectedStickies] = useState(() => {
  const savedSelectStates = localStorage.getItem(`selected-stickies-${selectedNoteId}`);
  return savedSelectStates ? JSON.parse(savedSelectStates) : {};
});

  // Create a function to register a sticky with the ZIndexManager
  const registerStickyWithZManager = useCallback((stickyId) => {
    //console.log(`Registering new sticky ${stickyId} with ZIndexManager (MAIN)`);
    const newZIndex = zManagerRef.current.addSticky(stickyId);
    //console.log(`Assigned z-index ${newZIndex} to sticky ${stickyId} (MAIN)`);
    return newZIndex;
  }, []);

  useEffect(() => {
    if (selectedNoteId && stickies && stickies.length > 0) {
        //console.log(`📌 Note changed! Loading stickies for note ${selectedNoteId}`);

        // Get current stickies in ZIndexManager
        const currentStickies = Array.from(zManagerRef.current._zIndices.keys());
        const newStickyIds = stickies.map(sticky => sticky.id);

        // Remove stickies that belong to the previous note
        const stickiesToRemove = currentStickies.filter(id => !newStickyIds.includes(id));

        if (stickiesToRemove.length > 0) {
            //console.log("🧹 Removing old stickies from ZIndexManager:", stickiesToRemove);
            stickiesToRemove.forEach(id => {
                zManagerRef.current._zIndices.delete(id);
            });

            // ✅ Completely replace the ZIndexManager instance!
            //console.log("🛑 Fully Resetting ZIndexManager...");
            zManagerRef.current = new ZIndexManagerMain(); // 🔥 Creates a fresh instance
        }

        // 📌 Adjust sticky positions if needed
        const stickiesWithoutMainCoords = stickies.filter(
            sticky => sticky.main_x === null || sticky.main_y === null
        );

        if (stickiesWithoutMainCoords.length > 0) {
            const stickyUpdates = stickiesWithoutMainCoords.map((sticky, index) => {
                const columnWidth = 250;
                const rowHeight = 200;
                const columnsPerRow = Math.floor(window.innerWidth / columnWidth);
                const row = Math.floor(index / columnsPerRow);
                const col = index % columnsPerRow;

                return {
                    id: sticky.id,
                    main_x: col * columnWidth + 100,
                    main_y: row * rowHeight + 150,
                    main_width: sticky.width || 150,
                    main_height: sticky.height || 150
                };
            });

            stickyUpdates.forEach(update => {
                updateSticky(update.id, update);
            });
        }

        // ✅ Delay re-initialization to allow React state updates
        console.log("🔍 ABOUT TO SET TIMEOUT - called from:", new Error().stack.split('\n')[2]);
        setTimeout(() => {
            console.log("🚀 Re-initializing stickies for new note");
            console.log("🚀 Re-initializing stickies for new note - STACK:", new Error().stack.split('\n')[1]);
            zManagerRef.current.initializeStickies(stickies);
        }, 10);
        setShowNotePreview(true);
    }
 }, [selectedNoteId, updateSticky]);

  // Add this to StickysViewMain.js
const handleFreezeLayout = () => {
  const stickyUpdates = stickies.map(sticky => ({
    id: sticky.id,
    x: sticky.x,
    y: sticky.y,
    width: sticky.width,
    height: sticky.height,
    main_x: sticky.main_x,
    main_y: sticky.main_y,
    main_width: sticky.main_width,
    main_height: sticky.main_height,
    display_mode: displayModes[sticky.id] || 'both',
    z_index: sticky.zIndex
  }));
  
  // Call the same saveStickyLayout function that the list panel uses
  saveStickyLayout(stickyUpdates);
};

  const handleStickyFocus = useCallback((stickyId) => {
    const sticky = stickies.find(s => s.id === stickyId);
    if (!sticky) return;

    const newZIndex = zManagerRef.current.bringToFront(stickyId);
    if (newZIndex !== null) {
      updateSticky(stickyId, { zIndex: newZIndex });
    }
  }, [stickies, updateSticky]);

  const handleAddSticky = useCallback(async () => {
    const topZIndex = zManagerRef.current._getTopZIndex() + 10;
    const newStickyId = await addSticky(topZIndex, stickyType);
    
    if (newStickyId) {
      const newZIndex = zManagerRef.current.addSticky(newStickyId);
      
      // Calculate position based on existing stickies with main coordinates
      const stickiesWithMainCoords = stickies.filter(
        sticky => sticky.main_x !== null && sticky.main_y !== null &&
                  (!displayModes[sticky.id] || displayModes[sticky.id] !== 'list')
      );
      const existingCount = stickiesWithMainCoords.length;
      
      const columnWidth = 250;
      const rowHeight = 200;
      const columnsPerRow = Math.floor(window.innerWidth / columnWidth);
      const row = Math.floor(existingCount / columnsPerRow);
      const col = existingCount % columnsPerRow;
      
      await updateSticky(newStickyId, {
          zIndex: newZIndex,
          type: stickyType,
          title: null,
          main_x: 75 + (existingCount * 80),   // Offset each one slightly  
          main_y: 100 + (existingCount * 70),      
          main_width: stickyType === 'checklist' ? 250 : 420,
          main_height: stickyType === 'checklist' ? 250 : 300,
          content: '' 
      });
         
    console.log('🚀 handleAddSticky updateSticky completed for sticky:', newStickyId);  

    handleStickyFocus(newStickyId);
  }
}, [stickyType, addSticky, updateSticky, handleStickyFocus]); // No stickies dependency!

  useEffect(() => {
    stickies.forEach((sticky) => {
      if (!zManagerRef.current._zIndices.has(sticky.id)) {
        const newZ = zManagerRef.current.addSticky(sticky.id);
        updateSticky(sticky.id, { zIndex: newZ });
      }
    });
  }, [stickies, updateSticky]);
  

  const handleSelectChange = useCallback((stickyId, isSelected) => {
  setSelectedStickies(prev => ({
    ...prev,
    [stickyId]: isSelected
  }));
  
  // Save to localStorage
  const newState = { ...selectedStickies, [stickyId]: isSelected };
  localStorage.setItem(`selected-stickies-${selectedNoteId}`, JSON.stringify(newState));
}, [selectedStickies, selectedNoteId]);

  const handleToggleDisplayMode = useCallback((stickyId, newMode) => {
    if (!selectedNoteId) return;

    updateSticky(stickyId, { 
      display_mode: newMode,
      note_id: selectedNoteId
    });

    setDisplayModes(prev => ({
      ...prev,
      [stickyId]: newMode
    }));
  }, [selectedNoteId, updateSticky]);

  const handleExpandFromList = useCallback((stickyId) => {
    handleStickyFocus(stickyId);
  }, [handleStickyFocus]);

  const handleSaveLayout = useCallback((stickyUpdates) => {
    saveStickyLayout(stickyUpdates);
  }, [saveStickyLayout]);

  // Helper function to choose the appropriate sticky component
  const getStickyComponent = (sticky) => {
    switch(sticky.type) {
      case 'checklist':
        return ChecklistSticky;
      default:
        return StickyNote;
    }
  };

  // Create filtered pinnedStickies object containing only current note's stickies
  // Create filtered selectedStickies object containing only current note's stickies
const currentSelectedStickies = {};
stickies?.forEach(sticky => {
  if (selectedStickies[sticky.id]) {
    currentSelectedStickies[sticky.id] = true;
  }
});

const getSelectedStickyIdsArray = () => {
  return Object.entries(currentSelectedStickies)
    .filter(([_, isSelected]) => isSelected)
    .map(([id]) => id);
};

const handleOpenAIModal = () => {
  setIsAICompositionModalOpen(true);
};

const handleSendToAI = (selectedStickies) => {
  console.log('Sending to AI from main panel:', selectedStickies);
  setIsAICompositionModalOpen(false);
};

return (
  <div className={`${styles.container} content-container`}>
    <div className={styles.controls}>
      <div className={styles.controlsWrapper}>
        <button
          className={styles.addButton}
          onClick={handleAddSticky}
        >
          Add Sticky
        </button>
        <button
      className={styles.addButton}  // You might want a different style class
      onClick={cycleArrangements}
    >
      Arrange: {arrangementMode}
    </button>

      </div>
    </div>
  
      <div className={styles.stickyContainer}>
      {/* Add the note preview sticky */}
  {showNotePreview && (
    <NotePreviewSticky
      selectedNote={getSelectedNote()}
      onClose={() => setShowNotePreview(false)}
      isInMainPanel={true}
    />
  )}
        {stickies?.map((sticky) => {
          if (displayModes[sticky.id] === 'list') return null;
  
          const StickyComponent = getStickyComponent(sticky);
          
          return (
            <StickyComponent
              key={sticky.id}
              sticky={sticky}
              updateSticky={updateSticky}
              removeSticky={(id) => {
                zManagerRef.current.removeSticky(id);
                removeSticky(id);
              }}
              maxZIndex={zManagerRef.current._getTopZIndex()}
              onFocus={() => handleStickyFocus(sticky.id)}
              onToggleDisplayMode={handleToggleDisplayMode}
              isSelected={Boolean(selectedStickies[sticky.id])} // Changed from isPinned
              onSelectChange={handleSelectChange} // Changed from onPinChange
              isInMainPanel={true}  // This indicates we're in the main panel
              addSticky={addSticky}
              registerWithZManager={registerStickyWithZManager}
              saveStickyLayout={handleSaveLayout}  // <-- Add this line
            />
          );
        })}
      </div>
  
      <StickiesListPanel
      isInMainPanel={true}  
      isRightPanelVisible={isRightPanelVisible}
      isLeftPanelVisible={isLeftPanelVisible} // Add this prop
      stickies={stickies || []}
      onExpandSticky={handleExpandFromList}
      displayModes={displayModes}
      onToggleDisplayMode={handleToggleDisplayMode}
      selectedStickies={selectedStickies} // Changed from pinnedStickies
      onSelectChange={handleSelectChange} // New prop
      onSaveLayout={handleSaveLayout}
      onRemoveSticky={removeSticky}  // ADD THIS LINE
      onReorderSticky={reorderStickies}  // ADD THIS LINE
      updateSticky={updateSticky}
    />
      <DraggableFreezeButton onFreeze={handleFreezeLayout} />
      <DraggableAIButton 

  key={`ai-button-${selectedNoteId}`} 
  onOpenAIModal={handleOpenAIModal} 
  pinnedCount={getSelectedStickyIdsArray().length}
/>

<AICompositionModal
  isOpen={isAICompositionModalOpen}
  onClose={() => setIsAICompositionModalOpen(false)}
  stickies={stickies || []}
  selectedStickyIds={getSelectedStickyIdsArray()}
  onSendToAI={handleSendToAI}
/>
    </div>
  );
  };
  
  export default React.memo(StickysViewMain);